import { useEffect, useState } from "react";
import axios from "axios";
import parse from "html-react-parser";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader, cleanO } from "../../utils/util";

// AI question generation for the admin library. Nothing is written until an
// admin accepts a proposal — the generator only ever proposes, so the answer
// key stays a human decision. Accepted questions go out through the same
// sam/add-question-group path the Excel import uses.
const MAX_FILE_MB = 10;
const ACCEPTED = /\.(pdf|txt|md|markdown|csv)$/i;

const AiGenerate = ({ category, topic, topics, onAdded, setShowModal }) => {
  const [material, setMaterial] = useState("");
  const [notes, setNotes] = useState("");
  const [count, setCount] = useState(10);
  const [chosenTopic, setChosenTopic] = useState(topic || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => { setChosenTopic(topic || ""); }, [topic]);

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) { setFile(null); return; }
    if (!ACCEPTED.test(f.name)) {
      toast("Attach a PDF, .txt, .md or .csv — or paste the text instead");
      e.target.value = ""; return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast(`That file is over the ${MAX_FILE_MB}MB limit`);
      e.target.value = ""; return;
    }
    setFile(f);
  };

  const generate = () => {
    if (!category) { toast("Select a category first"); return; }
    if (!chosenTopic) { toast("Choose a topic for these questions"); return; }
    if (!file && material.trim().length < 80) {
      toast("Paste a bit more of your material, or attach a file");
      return;
    }
    setBusy(true);
    let payload, config;
    if (file) {
      payload = new FormData();
      payload.append("category", category);
      payload.append("topic", chosenTopic);
      payload.append("count", String(count));
      payload.append("material", material);
      payload.append("notes", notes);
      payload.append("file", file);
      const h = getHeader();
      config = { headers: { ...h.headers, "content-type": "multipart/form-data" } };
    } else {
      payload = { category, topic: chosenTopic, count, material, notes };
      config = getHeader();
    }
    axios.post(API_ENDPOINT + "sam/generate-questions", payload, config)
      .then((res) => {
        setBusy(false);
        const d = res.data;
        if (!d.success) { toast(d.message || "Couldn't generate questions"); return; }
        const batch = d.message.questions || [];
        setProposals((prev) => prev.concat(batch));
        setMeta({
          model: d.message.model,
          requested: d.message.requested,
          returned: batch.length,
          discarded: d.message.discarded,
          truncated: d.message.truncated,
        });
        toast(`${batch.length} question${batch.length === 1 ? "" : "s"} to review`);
      })
      .catch(() => { setBusy(false); toast("Couldn't reach the generation service"); });
  };

  const discard = (id) => setProposals((p) => p.filter((q) => q.question_id !== id));

  const save = (list) => {
    if (!list.length) { toast("Nothing to save"); return; }
    setSaving(true);
    const body = {
      questions: list,
      topics: [{ category, topic: chosenTopic }],
      categories: [{ category }],
    };
    axios.post(API_ENDPOINT + "sam/add-question-group", body, getHeader())
      .then((res) => {
        setSaving(false);
        const d = res.data;
        if (d.success) {
          toast(`${list.length} question(s) added to ${category} / ${chosenTopic}`);
          const left = proposals.filter((q) => !list.some((l) => l.question_id === q.question_id));
          setProposals(left);
          if (onAdded) onAdded();
          // Nothing left to review — close rather than leaving an empty panel.
          if (left.length === 0 && setShowModal) setShowModal(false);
        } else { toast(d.message || "Couldn't save"); }
      })
      .catch(() => { setSaving(false); toast("Couldn't save the questions"); });
  };

  const topicOptions = (topics || []).map((t) => (typeof t === "string" ? t : t.topic));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="field">
          <label>Category</label>
          <input className="input" value={category || ""} readOnly />
        </div>
        <div className="field">
          <label>Topic</label>
          {topicOptions.length ? (
            <select className="input" value={chosenTopic} onChange={(e) => setChosenTopic(e.target.value)}>
              <option value="">Choose a topic…</option>
              {topicOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            <input className="input" value={chosenTopic} onChange={(e) => setChosenTopic(e.target.value)} placeholder="New topic name" />
          )}
        </div>
      </div>

      <div className="field">
        <label>Study material</label>
        <textarea className="input" rows={7} value={material} onChange={(e) => setMaterial(e.target.value)}
          placeholder="Paste the syllabus or notes these questions should come from. Questions are written only from what you give here." />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
        <div className="field">
          <label>Or attach a file</label>
          <input className="input" type="file" accept=".pdf,.txt,.md,.markdown,.csv" onChange={onFile} />
        </div>
        <div className="field">
          <label>Extra instruction (optional)</label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. focus on definitions" />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label>How many</label>
          <input className="input" type="number" min="1" max="25" value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(25, parseInt(e.target.value, 10) || 1)))} />
        </div>
      </div>

      <button className="btn btn-primary" onClick={generate} disabled={busy} style={{ justifyContent: "center" }}>
        {busy ? "Generating… this can take up to a minute" : "Generate questions"}
      </button>

      {proposals.length > 0 && (
        <>
          <hr className="hr" />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0 }}>Review &amp; accept</h4>
              {meta ? (
                <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {meta.returned} of {meta.requested} requested
                  {meta.discarded > 0 ? ` · ${meta.discarded} discarded as malformed` : ""}
                  {meta.truncated ? " · material was truncated" : ""} · {meta.model}
                </div>
              ) : null}
            </div>
            <button className="btn btn-secondary" onClick={() => setProposals([])} disabled={saving}>Discard all</button>
            <button className="btn btn-primary" onClick={() => save(proposals)} disabled={saving}>
              {saving ? "Saving…" : `Accept all ${proposals.length}`}
            </button>
          </div>

          <div style={{ display: "grid", gap: 10, maxHeight: "45vh", overflowY: "auto" }}>
            {proposals.map((q, i) => (
              <div key={q.question_id} style={{ background: "var(--color-bg)", border: "1px solid var(--color-divider)", padding: 14 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span className="tag tag-accent" style={{ flex: "none" }}>AI</span>
                  <div style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                    {i + 1}. {parse(cleanO(q.question))}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 4, marginTop: 10, marginLeft: 42, fontSize: 13 }}>
                  {q.options.map((o, j) => (
                    <div key={o.optionId || j} style={{ color: o.isCorrect ? "var(--color-accent)" : "inherit", fontWeight: o.isCorrect ? 600 : 400 }}>
                      {String.fromCharCode(65 + j)}. {parse(cleanO(o.option))}{o.isCorrect ? "  ✓" : ""}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10, marginLeft: 42 }}>
                  <button className="btn btn-secondary" onClick={() => save([q])} disabled={saving}>Accept</button>
                  <button className="btn btn-ghost" onClick={() => discard(q.question_id)} disabled={saving}>Discard</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AiGenerate;
